import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Grid, Typography } from "@mui/material";
import {
  deleteSiteFromTable,
  getSitesWithFilterAndPagination,
} from "../../services/siteServices/mainSiteService";
import FilterModal from "../../Modals/FilterModal";
import { useNavigate } from "react-router-dom";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import DynamicTable from "../../components/Tables/DynamicTable";
import { AuthContext } from "../../contexts/AuthContext";
import DeleteConfirmationModal from "../../Modals/DeleteConfirmationModal";
import SnackBars from "../../components/snackbars/SnackBars";

export default function Projects() {
  const [sites, setSites] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [typeDelete, setTypeDelete] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalSitesCount, setTotalSitesCount] = useState(0);
  const { subsection, setSubsection } = useContext(AuthContext);
  const [filters, setFilters] = useState([
    { field: "", action: "", value: "" },
  ]);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState({
    title: "",
    type: "",
  });
  const navigate = useNavigate();
  const hasValidFilters = () => {
    return filters.some(
      (filter) => filter.field && filter.action && filter.value
    );
  };

  useEffect(() => {
    setFilters((prevFilters) => [
      { field: "partner_name", action: "equals", value: subsection }, // New filter
    ]);
  }, [subsection]);

  const fetchSites = async () => {
    try {
      const activeFilters = hasValidFilters() ? filters : [];
      const res = await getSitesWithFilterAndPagination(
        pagination.pageIndex + 1,
        pagination.pageSize,
        activeFilters
      );
      setSites(res.data);
      setTotalSitesCount(res.totalSites);
    } catch (error) {
      console.error("Error fetching sites:", error);
    }
  };

  useEffect(() => {
    fetchSites();
  }, [pagination.pageIndex, pagination.pageSize, filters]);

  const handleAddNewProject = () => {
    navigate("/projects/add-new");
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRemoveFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleApply = () => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
    fetchSites();
    setAnchorEl(null);
  };

  const handleCloseAnchor = () => {
    setFilters([
      { field: "partner_name", action: "equals", value: subsection },
    ]);
    setAnchorEl(null);
  };

  const handleNavigate = (rowData) => {
    navigate(`/projects/${rowData.id}`);
  };

  const handleSiteDeleteDialog = (rowData) => {
    setSiteToDelete(rowData);
    setDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setTypeDelete("");
    setDeleteDialog(false);
    setSiteToDelete(null);
  };

  const handleSiteDelete = async () => {
    if (siteToDelete) {
      try {
        const result = await deleteSiteFromTable(siteToDelete?.id);
        if (result.status === 200) {
          await fetchSites();
          setSnackBarMessage({
            title: "Site deleted successfully",
            type: "success",
          });
          setOpenSnackBar(true);
        } else {
          throw new Error("Failed to delete site");
        }
      } catch (error) {
        setSnackBarMessage({
          title: "Error deleting site",
          type: "error",
        });
        setOpenSnackBar(true);
        console.error("Error deleting site:", error);
      }
    }
    setTypeDelete("");
    setDeleteDialog(false);
    setSiteToDelete(null);
  };
  const columns = useMemo(
    () => [
      {
        accessorKey: "project_code",
        header: "ID",
        size: 150,
        Cell: ({ row }) => (
          <Typography
            sx={{
              cursor: "pointer",
              fontSize: "14px",
              color: "#1976D2",
              "&:hover": {
                color: "#1976D2",
              },
            }}
            onClick={() => handleNavigate(row.original)}
          >
            {row.original.project_code}
          </Typography>
        ),
      },

      {
        accessorKey: "ProjectName",
        header: "Principle",
        size: 150,
      },
      {
        header: "City-State",
        accessorFn: (row) => `${row.CityName} - ${row.StateName}`,
        id: "cityState",
        size: 150,
      },
      {
        accessorKey: "Profile",
        header: "Profile",
        size: 150,
      },
      {
        accessorKey: "LocationName",
        header: "Location Name",
        size: 200,
      },
      {
        accessorKey: "LocationLandmark",
        header: "Landmark",
        size: 200,
      },

      {
        accessorKey: "project_status",
        header: "Status",
        size: 150,
      },
      {
        accessorKey: "action",
        header: "Action",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Button
            variant="outlined"
            size="small"
            sx={{
              color: "#b41660",
              borderColor: "#b41660",
              "&:hover": {
                backgroundColor: "#f0e7f0",
                borderColor: "#a00e50",
                color: "#a00e50",
              },
            }}
            onClick={() => handleSiteDeleteDialog(row.original)}
          >
            Delete
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={12} md={6}>
          <Button
            variant="contained"
            size="small"
            onClick={handleAddNewProject}
            sx={{
              width: { xs: "100%", lg: "20%", md: "40%", sm: "100%" },
              borderColor: "#b41660",
              backgroundColor: "#b41660",
              "&:hover": {
                backgroundColor: "#a00e50",
              },
            }}
          >
            Add New Sites
          </Button>
        </Grid>
        <Grid item xs={12} sm={12} md={6} container justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            onClick={handleClick}
            sx={{
              width: { xs: "100%", lg: "20%", md: "40%", sm: "100%" },
              borderColor: "#b41660",
              color: "#b41660",
              "&:hover": {
                backgroundColor: "#f0e7f0",
              },
            }}
          >
            Filter{" "}
            {hasValidFilters() ? <FilterAltIcon /> : <FilterAltOffIcon />}
          </Button>
          <FilterModal
            setAnchorEl={setAnchorEl}
            anchorEl={anchorEl}
            setFilters={setFilters}
            filters={filters}
            handleRemoveFilter={handleRemoveFilter}
            handleClose={handleCloseAnchor}
            handleApply={handleApply}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <DynamicTable
            columns={columns}
            data={sites}
            totalSitesCount={totalSitesCount}
            setPagination={setPagination}
            pagination={pagination}
            isEnablePagination={true}
            isFilterEnable={true}
            isSearchBarEnable={true}
            isEnableSorting={true}
            isHeaderBackgroundColor={false}
            showTableTopToolbar={true}
            enableColumnActions={true}
          />
        </Grid>
      </Grid>
      <DeleteConfirmationModal
        typeDelete={typeDelete}
        setTypeDelete={setTypeDelete}
        open={deleteDialog}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleSiteDelete}
        siteData={siteToDelete}
      />
      <SnackBars
        open={openSnackBar}
        setOpen={setOpenSnackBar}
        handleClose={() => setOpenSnackBar(false)}
        title={snackBarMessage.title}
        type={snackBarMessage.type}
      />
    </>
  );
}
